import type { TypedDocumentNode } from '@apollo/client';
import { gql, useLazyQuery } from '@apollo/client';
import { useRef, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Dialog } from '@sorare/core/src/components/dialog';
import { FOOTBALL_ONBOARDING_COMPOSE_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { convertObjectId } from '@sorare/core/src/gql/convertObjectId';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useCreateAndLoadWallet } from '@sorare/core/src/hooks/wallets/useCreateAndLoadWallet';
import { isTypeFn } from '@sorare/core/src/lib/gql';
import { getSortedCards } from '@sorare/core/src/lib/players';

import { CartPayment } from '@sorare/marketplace/src/components/cart/CartPayment';
import { useBuyConfirmationContext } from '@sorare/marketplace/src/contexts/buyingConfirmation';

import { useComposeInit } from '@sorare/gameplay/src/hooks/compose/useComposeInit';
import { useComposeTeamAutosave } from '@sorare/gameplay/src/hooks/compose-team/useComposeTeamAutosave';

import { CaptainTutorial } from './CaptainTutorial';
import { FirstLineupEdition } from './FirstLineupEdition';
import type {
  FirstLineupBoughtCardsQuery,
  FirstLineupBoughtCardsQueryVariables,
  FirstLineup_currentUser,
  FirstLineup_so5Leaderboard,
} from './__generated__/index.graphql';

const CartPaymentWrapper = styled(Vertical)`
  padding: var(--double-unit);
`;

const FIRST_LINEUP_BOUGHT_CARDS_QUERY = gql`
  query FirstLineupBoughtCardsQuery($id: String!) {
    tokens {
      primaryOffer(id: $id) {
        id
        anyCards {
          slug
          anyPositions
          pictureUrl
          ... on Card {
            slug
            id
          }
        }
      }
    }
  }
` as TypedDocumentNode<
  FirstLineupBoughtCardsQuery,
  FirstLineupBoughtCardsQueryVariables
>;

type Props = {
  currentUser?: FirstLineup_currentUser | null;
  so5Leaderboard?: FirstLineup_so5Leaderboard;
  backUrl: string;
  refetch: () => Promise<any>;
  cartQueryLoading?: boolean;
};
export const FirstLineup = ({
  currentUser,
  so5Leaderboard,
  backUrl,
  refetch,
  cartQueryLoading,
}: Props) => {
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const { composeSessionId } = useComposeInit(so5Leaderboard?.slug || '');
  const { addItemsToAutoSave } = useComposeTeamAutosave(
    so5Leaderboard?.slug || '',
    composeSessionId
  );
  const navigate = useNavigate();
  const [getBoughtCards, { loading: boughtCardsLoading }] = useLazyQuery(
    FIRST_LINEUP_BOUGHT_CARDS_QUERY
  );
  const [paymentDone, setPaymentDone] = useState(false);
  const [boughtCardIds, setBoughtCardIds] = useState<string[]>([]);
  const [cardBoughtPictureUrl, setCardBoughtPictureUrl] = useState<string>();
  const { buyConfirmationProps } = useBuyConfirmationContext();
  const createAndLoadWallet = useCreateAndLoadWallet();

  const redirectToComposeTeam = () => {
    addItemsToAutoSave(
      boughtCardIds.map((id, index) => ({
        position: index,
        benchObjectId: convertObjectId(id, 'ComposeTeamBenchCard'),
      }))
    );

    navigate(
      `${generatePath(FOOTBALL_ONBOARDING_COMPOSE_SLUG, {
        slug: so5Leaderboard?.slug || '',
      })}?marketGuidance=true`
    );
  };

  const onPaymentSuccessCalledRef = useRef(false);
  const onPaymentSuccess = async (offerId: string) => {
    if (onPaymentSuccessCalledRef.current) return;
    onPaymentSuccessCalledRef.current = true;
    const { data } = await getBoughtCards({
      variables: { id: idFromObject(offerId) },
    });
    const cards = data?.tokens.primaryOffer.anyCards.filter(isTypeFn('Card'));
    if (!cards) return;
    const orderedCards = getSortedCards(cards);
    setBoughtCardIds(orderedCards.map(({ id }) => id));
    // Use the forward as the example card
    setCardBoughtPictureUrl(orderedCards.at(3)?.pictureUrl || undefined);
  };

  if (
    paymentDone &&
    buyConfirmationProps?.primaryBuyId &&
    !boughtCardIds.length &&
    !boughtCardsLoading
  ) {
    onPaymentSuccess(buyConfirmationProps.primaryBuyId);
  }

  if (!boughtCardIds.length) {
    return (
      <>
        <FirstLineupEdition
          progress={95}
          currentUser={currentUser}
          so5Leaderboard={so5Leaderboard}
          onSubmit={() => {
            setPaymentDialogOpen(true);
          }}
          backUrl={backUrl}
          refetch={refetch}
          cartQueryLoading={cartQueryLoading}
        />
        <Dialog
          open={paymentDialogOpen}
          onClose={() => setPaymentDialogOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <CartPaymentWrapper>
            {!!currentUser?.myCart && (
              <CartPayment
                myCart={currentUser.myCart}
                onSuccess={() => {
                  setPaymentDone(true);
                }}
                beforeSubmitCallback={createAndLoadWallet}
                primaryBuyConfirmationOptions={{
                  hideSecondaryOrTickets: true,
                }}
              />
            )}
          </CartPaymentWrapper>
        </Dialog>
      </>
    );
  }

  if (boughtCardsLoading) {
    return <LoadingIndicator fullHeight />;
  }

  return (
    <CaptainTutorial
      progress={98}
      firstCardPictureUrl={cardBoughtPictureUrl}
      onSubmit={redirectToComposeTeam}
    />
  );
};

FirstLineup.fragments = {
  currentUser: gql`
    fragment FirstLineup_currentUser on CurrentUser {
      slug
      myCart(sport: $sport) {
        id
        mainCartItems {
          id
          card {
            slug
            ... on Card {
              id
              slug
              player {
                slug
                pictureUrl
              }
            }
          }
        }
        ...CartPayment_cart
      }
      ...FirstLineupEdition_currentUser
    }
    ${FirstLineupEdition.fragments.currentUser}
    ${CartPayment.fragments.cart}
  ` as TypedDocumentNode<FirstLineup_currentUser>,
  so5Leaderboard: gql`
    fragment FirstLineup_so5Leaderboard on So5Leaderboard {
      slug
      so5Fixture {
        slug
        type
      }
      ...FirstLineupEdition_so5Leaderboard
    }
    ${FirstLineupEdition.fragments.so5Leaderboard}
  ` as TypedDocumentNode<FirstLineup_so5Leaderboard>,
};
